import { addYears, formatISO } from "date-fns";
import type { ServiceTemplateRecurrence } from "@common/schemas/serviceTemplateRecurrence.schema";
import {
    MonthRecurrenceType,
    ServiceTemplateRecurrenceStatus,
} from "@common/types/serviceTemplateRecurrence";
import {
    generateServiceTemplateSchedule,
    type ScheduledOccurrence,
} from "@common/helpers/serviceTemplateRecurrence";
import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import type { ScheduleDefaults } from "./applyCloseTemplate.schema";
import type { ServiceTemplateScheduleOccurrenceRow } from "./serviceTemplateScheduleOccurrencesViewConfig";

const PREVIEW_OCCURRENCE_COUNT = 48;
const PREVIEW_PLACEHOLDER_UUID = "00000000-0000-0000-0000-000000000000";

/** Fills in the identity/audit fields the recurrence type needs but the date math never reads, so the wizard can preview occurrences before the schedule is created. Caller must ensure `schedule.period` is non-null. */
function toPreviewRecurrence(
    schedule: ScheduleDefaults & {
        period: NonNullable<ScheduleDefaults["period"]>;
    },
): ServiceTemplateRecurrence {
    const isDateOfMonthPeriod =
        schedule.period === ServiceTemplateRecurrencePeriod.QUARTERLY ||
        schedule.period === ServiceTemplateRecurrencePeriod.ANNUALLY;

    return {
        id: PREVIEW_PLACEHOLDER_UUID,
        organizationId: PREVIEW_PLACEHOLDER_UUID,
        workspaceId: null,
        templateId: PREVIEW_PLACEHOLDER_UUID,
        entityUuid: PREVIEW_PLACEHOLDER_UUID,
        status: ServiceTemplateRecurrenceStatus.ACTIVE,
        period: schedule.period,
        interval: schedule.interval,
        startDate: schedule.startDate,
        endDate: schedule.endDate,
        generatedUntil: null,
        maxOccurrenceCount: schedule.maxOccurrenceCount,
        applicationLeadValue: schedule.applicationLeadValue,
        applicationLeadUnit: schedule.applicationLeadUnit,
        weekDay: null,
        weekDays:
            schedule.period === ServiceTemplateRecurrencePeriod.WEEKLY
                ? schedule.weekDays
                : null,
        month: schedule.month,
        dateOfMonth: isDateOfMonthPeriod ? schedule.dateOfMonth : null,
        datesOfMonth:
            schedule.period === ServiceTemplateRecurrencePeriod.MONTHLY
                ? schedule.datesOfMonth
                : null,
        monthRecurrenceType:
            isDateOfMonthPeriod && schedule.dateOfMonth != null
                ? MonthRecurrenceType.DAY
                : null,
        quarterMonth: schedule.quarterMonth,
        workStartDateMode: schedule.workStartDateMode,
        workStartOffsetDays: schedule.workStartOffsetDays,
        workStartSkipWeekend: schedule.workStartSkipWeekend,
        workStartWeekday: schedule.workStartWeekday,
        workStartRelativePeriod: null,
        workStartRelativePosition: null,
        workStartRelativeDayType: null,
        workStartRelativeDayOfPeriod: null,
        workEndDateMode: schedule.workEndDateMode,
        workEndOffsetDays: schedule.workEndOffsetDays,
        workEndRelativePeriod: schedule.workEndRelativePeriod,
        workEndRelativePosition: schedule.workEndRelativePosition,
        workEndRelativeDayType: schedule.workEndRelativeDayType,
        workEndRelativeDayOfPeriod: schedule.workEndRelativeDayOfPeriod,
        preparerId: null,
        reviewerId: null,
        managerId: null,
        clientId: null,
        createdByUserId: null,
        updatedByUserId: null,
        createdAt: new Date(),
        updatedAt: new Date(),
    };
}

function toIsoDateString(date: Date): string {
    return formatISO(date, { representation: "date" });
}

function toOccurrenceRows(
    occurrences: ScheduledOccurrence[],
): ServiceTemplateScheduleOccurrenceRow[] {
    return occurrences.map((occurrence) => ({
        id: String(occurrence.index),
        index: occurrence.index,
        applicationDate: toIsoDateString(occurrence.applicationDate),
        serviceStartDate: toIsoDateString(occurrence.serviceStartDate),
        serviceEndDate: toIsoDateString(occurrence.serviceEndDate),
        workStartDate: toIsoDateString(occurrence.workStartDate),
        workEndDate: toIsoDateString(occurrence.workEndDate),
    }));
}

export function computeScheduleWizardPreviewOccurrences(
    schedule: ScheduleDefaults,
): ServiceTemplateScheduleOccurrenceRow[] {
    if (!schedule.period) {
        return [];
    }
    const narrowedSchedule = { ...schedule, period: schedule.period };
    try {
        const occurrences = generateServiceTemplateSchedule({
            recurrence: toPreviewRecurrence(narrowedSchedule),
            fromDate: addYears(new Date(), 10),
            count: PREVIEW_OCCURRENCE_COUNT,
        });
        return toOccurrenceRows(occurrences);
    } catch {
        return [];
    }
}
